import { report } from '@/api/track.js'
import { system } from '@/api/system.js'
import { resolveRouteByPath } from './trackConfig.js'

const SESSION_KEY = 'css_track_session_id'
const FLUSH_INTERVAL_MS = 2000
const FLUSH_BATCH_SIZE = 10
/** 单次页面停留上限 30 分钟，避免挂机标签页上报超长时长 */
const MAX_STAY_MS = 30 * 60 * 1000

/** @type {Array} */
let queue = []
/** @type {ReturnType<typeof setInterval>|null} */
let flushTimer = null

/** 当前白名单页访问上下文 */
let currentVisit = null

function currentLocationPath() {
  if (typeof window === 'undefined' || !window.location) return ''
  return window.location.pathname || ''
}

/**
 * 按钮点击前确保已有页面访问上下文。
 * 路由守卫偶发未建立 currentVisit 时，用 v-track 的 module/page 或当前 URL 兜底补建。
 * 若按钮显式声明的模块/页面与当前 visit 不一致，切换 visit，避免串到上一页模块。
 */
function ensureVisitForButton(opts) {
  const v = opts || {}
  let moduleCode = v.module || v.moduleCode
  let pageCode = v.page || v.pageCode
  let routePath = v.routePath || currentLocationPath()

  if (!moduleCode || !pageCode) {
    const meta = resolveRouteByPath(routePath)
    if (meta) {
      moduleCode = meta.moduleCode
      pageCode = meta.pageCode
    }
  }

  if (
    currentVisit &&
    currentVisit.moduleCode &&
    (!moduleCode ||
      !pageCode ||
      (currentVisit.moduleCode === moduleCode && currentVisit.pageCode === pageCode))
  ) {
    return
  }

  if (!moduleCode || !pageCode) return

  pageEnter({
    routePath: routePath || moduleCode + '/' + pageCode,
    moduleCode: moduleCode,
    pageCode: pageCode
  })
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

/** yyyy-MM-dd HH:mm:ss，匹配后端 @JsonFormat GMT+8 */
function formatClientTime(date) {
  const d = date || new Date()
  return (
    d.getFullYear() +
    '-' +
    pad2(d.getMonth() + 1) +
    '-' +
    pad2(d.getDate()) +
    ' ' +
    pad2(d.getHours()) +
    ':' +
    pad2(d.getMinutes()) +
    ':' +
    pad2(d.getSeconds())
  )
}

function genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getSessionId() {
  return sessionStorage.getItem(SESSION_KEY)
}

export function ensureSession() {
  let id = getSessionId()
  if (!id) {
    id = genId()
    sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

function reportUrl() {
  return system.requestBaseUrl + system.cssServerName + '/track/report'
}

function authHeader() {
  const token = sessionStorage.getItem('access_token')
  return token ? 'Bearer ' + token : ''
}

/**
 * 普通上报（独立 axios，带 Authorization，不触发全局 loading）
 */
function postEvents(events) {
  if (!events || !events.length) return Promise.resolve(null)
  return report(events).catch(function () {
    return null
  })
}

/**
 * 页面离开优先 keepalive，保证卸载时也能带 Authorization。
 * sendBeacon 无法自定义 Authorization，故跳过；失败再回退 axios。
 */
function postEventsKeepalive(events) {
  if (!events || !events.length) return
  const body = JSON.stringify({ events })
  const url = reportUrl()
  const authorization = authHeader()

  if (typeof fetch === 'function') {
    try {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          Authorization: authorization
        },
        body: body,
        keepalive: true,
        credentials: 'omit'
      }).catch(function () {
        postEvents(events)
      })
      return
    } catch (e) {
      // fall through
    }
  }

  // 无 Authorization 能力时再尝试 beacon（通常会被后端 401）
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json;charset=utf-8' })
      if (navigator.sendBeacon(url, blob)) {
        return
      }
    } catch (e) {
      // fall through
    }
  }

  postEvents(events)
}

function ensureFlushTimer() {
  if (flushTimer) return
  flushTimer = setInterval(function () {
    flushQueue()
  }, FLUSH_INTERVAL_MS)
}

function flushQueue() {
  if (!queue.length) return
  const batch = queue.splice(0, queue.length)
  postEvents(batch)
}

function enqueue(event) {
  queue.push(event)
  ensureFlushTimer()
  if (queue.length >= FLUSH_BATCH_SIZE) {
    flushQueue()
  }
}

function baseFields(extra) {
  return Object.assign(
    {
      sessionId: ensureSession(),
      clientTime: formatClientTime()
    },
    extra || {}
  )
}

/**
 * 进入白名单页
 * @param {{ routePath: string, moduleCode: string, pageCode: string }} meta
 */
export function pageEnter(meta) {
  if (!meta || !meta.moduleCode) return
  // 同模块同页面已在访问中则不再重复 enter（避免路由守卫 + 页面生命周期双触发）
  if (
    currentVisit &&
    currentVisit.moduleCode === meta.moduleCode &&
    currentVisit.pageCode === meta.pageCode
  ) {
    return
  }
  // 若仍有未离开的访问，先 leave（路由守卫通常已处理）
  if (currentVisit) {
    pageLeave()
  }

  const clientEventId = genId()
  const event = baseFields({
    clientEventId: clientEventId,
    eventKind: 'page_enter',
    moduleCode: meta.moduleCode,
    pageCode: meta.pageCode,
    routePath: meta.routePath
  })

  currentVisit = {
    clientEventId: clientEventId,
    pageVisitId: null,
    moduleCode: meta.moduleCode,
    pageCode: meta.pageCode,
    routePath: meta.routePath,
    enterAt: Date.now()
  }

  postEvents([event]).then(function (res) {
    if (!res || res.code !== 200 || !res.data || !res.data.pageVisitIds) return
    const list = res.data.pageVisitIds
    for (let i = 0; i < list.length; i++) {
      if (list[i] && list[i].clientEventId === clientEventId) {
        if (currentVisit && currentVisit.clientEventId === clientEventId) {
          currentVisit.pageVisitId = list[i].pageVisitId
        }
        break
      }
    }
  })
}

/**
 * 离开当前页（立即 flush 队列 + leave 事件）
 */
export function pageLeave() {
  flushQueue()

  if (!currentVisit) return

  const visit = currentVisit
  currentVisit = null

  let stayMs =
    visit.enterAt != null ? Math.max(0, Date.now() - visit.enterAt) : undefined
  if (stayMs != null && stayMs > MAX_STAY_MS) {
    stayMs = MAX_STAY_MS
  }

  const event = baseFields({
    clientEventId: genId(),
    eventKind: 'page_leave',
    moduleCode: visit.moduleCode,
    pageCode: visit.pageCode,
    routePath: visit.routePath,
    pageVisitId: visit.pageVisitId || undefined,
    stayMs: stayMs
  })

  postEventsKeepalive([event])
}

/**
 * 仅当当前访问属于指定模块/页面时 leave。
 * 用于页面 beforeDestroy：避免销毁晚于新页 enter 时误清掉新页访问。
 */
export function pageLeaveIfMatch(moduleCode, pageCode) {
  if (
    !currentVisit ||
    currentVisit.moduleCode !== moduleCode ||
    currentVisit.pageCode !== pageCode
  ) {
    return
  }
  pageLeave()
}

/**
 * 按钮点击
 * @param {{ code?: string, buttonCode?: string, name?: string, buttonName?: string, extraJson?: string|object }} opts
 */
export function click(opts) {
  enqueueButtonEvent('click', opts)
}

/**
 * 搜索类按钮
 */
export function search(opts) {
  enqueueButtonEvent('search', opts)
}

function enqueueButtonEvent(eventKind, opts) {
  const v = opts || {}
  ensureVisitForButton(v)
  if (!currentVisit || !currentVisit.moduleCode) return

  let extraJson = v.extraJson
  if (extraJson != null && typeof extraJson !== 'string') {
    try {
      extraJson = JSON.stringify(extraJson)
    } catch (e) {
      extraJson = undefined
    }
  }

  // 显式 v-track module/page 优先，保证项目交互管理等页统计编码正确
  const moduleCode = v.module || v.moduleCode || currentVisit.moduleCode
  const pageCode = v.page || v.pageCode || currentVisit.pageCode

  enqueue(
    baseFields({
      clientEventId: genId(),
      eventKind: eventKind,
      moduleCode: moduleCode,
      pageCode: pageCode,
      routePath: currentVisit.routePath || currentLocationPath(),
      pageVisitId: currentVisit.pageVisitId || undefined,
      buttonCode: v.code || v.buttonCode,
      buttonName: v.name || v.buttonName,
      extraJson: extraJson
    })
  )
}

export function getCurrentVisit() {
  return currentVisit
}

const track = {
  getSessionId,
  ensureSession,
  pageEnter,
  pageLeave,
  pageLeaveIfMatch,
  click,
  search,
  flushQueue,
  getCurrentVisit
}

export default track



// WEBPACK FOOTER //
// ./src/utils/track/track.js